import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Save, Search, FileDown, FileUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { api, errMsg, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CommissionGaps } from "@/components/CommissionGaps";

const clone = (o) => JSON.parse(JSON.stringify(o));
const isIndiv = (below) => below === "individual" || below == null || Number.isNaN(Number(below));

export const CommissionSettings = ({ onSaved, initialQuery, onOpenOrder }) => {
  const [products, setProducts] = useState([]);
  const [rules, setRules] = useState({});
  const [busy, setBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [q, setQ] = useState(initialQuery || "");
  const [savedTick, setSavedTick] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => { if (initialQuery) setQ(initialQuery); }, [initialQuery]);
  useEffect(() => {
    if (initialQuery && products.length) {
      const el = document.querySelector(`[data-testid="commission-card-${initialQuery}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [initialQuery, products]);

  useEffect(() => {
    api.get("/settings/commissions")
      .then((r) => { setProducts(r.data.products); setRules(r.data.rules || {}); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const ruleFor = (sku) => rules[sku] || { tiers: [], below: "individual" };
  const update = (sku, fn) => setRules((prev) => {
    const next = clone(prev);
    next[sku] = next[sku] || { tiers: [], below: "individual" };
    fn(next[sku]);
    return next;
  });
  const addTier = (sku) => update(sku, (r) => { r.tiers = r.tiers || []; r.tiers.push({ min_price: "", commission: "" }); });
  const removeTier = (sku, i) => update(sku, (r) => { r.tiers.splice(i, 1); });
  const setTier = (sku, i, f, v) => update(sku, (r) => { r.tiers[i][f] = v; });
  const setBelowMode = (sku, mode) => update(sku, (r) => { r.below = mode === "individual" ? "individual" : 0; });
  const setBelowValue = (sku, v) => update(sku, (r) => { r.below = v; });
  const setHaft = (sku, f, v) => update(sku, (r) => { r.haft = r.haft || { przod: 0, tyl: 0, oba: 0 }; r.haft[f] = v; });

  const confirm = useConfirm();

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const tid = toast.loading("Wczytywanie progów z pliku…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/commissions/import/xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imported = r.data.rules || {};
      setRules((prev) => ({ ...prev, ...imported }));
      toast.success(`Wczytano progi dla ${r.data.count} modeli — kliknij „Zapisz ustawienia”, aby zapisać.`, { id: tid, duration: 6000 });
    } catch (err) {
      toast.error(errMsg(err), { id: tid });
    }
  };

  const save = async () => {
    if (!(await confirm({ title: "Zapisać ustawienia prowizji?", description: "Zapisać progi cenowe i prowizje dla wszystkich modeli?", confirmText: "Zapisz" }))) return;
    setBusy(true);
    try {
      const payload = { rules: {} };
      for (const [sku, r] of Object.entries(rules)) {
        const rule = {
          tiers: (r.tiers || [])
            .map((t) => ({ min_price: Number(t.min_price) || 0, commission: Number(t.commission) || 0 }))
            .sort((a, b) => b.min_price - a.min_price),
          below: isIndiv(r.below) ? "individual" : Number(r.below),
        };
        if (r.haft) rule.haft = { przod: Number(r.haft.przod) || 0, tyl: Number(r.haft.tyl) || 0, oba: Number(r.haft.oba) || 0 };
        payload.rules[sku] = rule;
      }
      await api.put("/settings/commissions", payload);
      toast.success("Zapisano ustawienia prowizji");
      setSavedTick((t) => t + 1);
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const applyUnsettled = async () => {
    if (!(await confirm({ title: "Zastosować progi do nierozliczonych zamówień?", description: "Bieżące progi prowizji zostaną przeliczone we WSZYSTKICH historycznych zamówieniach, które NIE są jeszcze rozliczone. Zamówienia już rozliczone pozostaną bez zmian. Operacja nie zmienia cen ani wartości netto.", confirmText: "Zastosuj" }))) return;
    setApplyBusy(true);
    try {
      const r = await api.post("/settings/commissions/apply-unsettled");
      const n = r.data.recalculated_orders;
      const label = n === 1 ? "1 nierozliczonym zamówieniu" : `${n} nierozliczonych zamówieniach`;
      toast.success(`Przeliczono prowizję w ${label}`, { duration: 6000 });
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setApplyBusy(false); }
  };

  const addTiers = (sku) => {
    setQ(sku);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="commission-card-${sku}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  return (
    <div data-testid="commission-settings" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-amber-400">Ustawienia prowizji</p>
          <p className="text-sm font-medium text-muted-foreground">Ustal progi cenowe i prowizje dla każdego modelu. Możesz dodać dowolną liczbę progów.</p>
        </div>
        <Button data-testid="save-commissions" onClick={save} disabled={busy} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold uppercase tracking-wider">
          <Save className="h-4 w-4" />{busy ? "Zapisywanie…" : "Zapisz ustawienia"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="export-commissions-xlsx" variant="outline" size="sm" className="gap-2 font-semibold"
          onClick={() => downloadBlob("/settings/commissions/export/xlsx", "progi-prowizji.xlsx", "XLSX")}>
          <FileDown className="h-4 w-4" />Eksportuj progi (XLSX)
        </Button>
        <Button data-testid="import-commissions-xlsx" variant="outline" size="sm" className="gap-2 font-semibold"
          onClick={() => fileRef.current?.click()}>
          <FileUp className="h-4 w-4" />Importuj progi (XLSX)
        </Button>
        <input ref={fileRef} data-testid="import-commissions-input" type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden" onChange={onImportFile} />
        <span className="text-xs font-medium text-muted-foreground">Import scala wczytane progi z obecnymi — zapis po kliknięciu „Zapisz ustawienia”.</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sky-400/30 bg-sky-400/5 p-3">
        <Button data-testid="apply-commissions-unsettled" variant="outline" size="sm" disabled={applyBusy} className="gap-2 font-semibold border-sky-400/50 text-sky-400 hover:bg-sky-400/10"
          onClick={applyUnsettled}>
          <RefreshCw className={`h-4 w-4 ${applyBusy ? "animate-spin" : ""}`} />{applyBusy ? "Przeliczanie…" : "Zastosuj progi do nierozliczonych zamówień"}
        </Button>
        <span className="text-xs font-medium text-muted-foreground">Nalicza bieżące progi w historycznych zamówieniach, które nie zostały jeszcze rozliczone. Rozliczone pozostają nietknięte.</span>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="commission-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj modelu: nazwa, SKU, typ…" className="pl-9" />
      </div>

      <CommissionGaps onAddTiers={addTiers} onOpenOrder={onOpenOrder} reloadSignal={savedTick} />

      <div className="grid lg:grid-cols-2 gap-4">
        {products.filter((p) => `${p.sku} ${p.name} ${p.type}`.toLowerCase().includes(q.toLowerCase())).map((p) => {
          const r = ruleFor(p.sku);
          const belowMode = isIndiv(r.below) ? "individual" : "fixed";
          return (
            <div key={p.sku} data-testid={`commission-card-${p.sku}`} className="rounded-2xl border border-border bg-card p-5 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-bold text-amber-300">{p.sku}</span>
                <span className="font-display text-base font-extrabold">{p.name}</span>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wide">Progi: cena netto/szt. → prowizja</Label>
                {(r.tiers || []).length === 0 && <p className="text-xs font-medium text-muted-foreground">Brak progów — dodaj pierwszy.</p>}
                {(r.tiers || []).map((t, i) => (
                  <div key={i} data-testid={`tier-${p.sku}-${i}`} className="flex items-center gap-2">
                    <span className="text-sm font-bold text-muted-foreground">od</span>
                    <Input data-testid={`tier-min-${p.sku}-${i}`} type="number" step="0.01" value={t.min_price} onChange={(e) => setTier(p.sku, i, "min_price", e.target.value)} className="w-24 font-mono font-semibold" placeholder="0" />
                    <span className="text-sm font-bold text-muted-foreground">zł →</span>
                    <Input data-testid={`tier-comm-${p.sku}-${i}`} type="number" step="0.01" value={t.commission} onChange={(e) => setTier(p.sku, i, "commission", e.target.value)} className="w-24 font-mono font-semibold" placeholder="0" />
                    <span className="text-sm font-bold text-amber-300">zł</span>
                    <Button data-testid={`tier-remove-${p.sku}-${i}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-400 ml-auto" onClick={() => removeTier(p.sku, i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button data-testid={`tier-add-${p.sku}`} variant="outline" size="sm" className="gap-2 border-dashed font-semibold" onClick={() => addTier(p.sku)}><Plus className="h-4 w-4" />Dodaj próg</Button>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wide">Poniżej najniższego progu</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={belowMode} onValueChange={(v) => setBelowMode(p.sku, v)}>
                    <SelectTrigger data-testid={`below-mode-${p.sku}`} className="w-[210px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Indywidualne ustalenie</SelectItem>
                      <SelectItem value="fixed">Stała prowizja</SelectItem>
                    </SelectContent>
                  </Select>
                  {belowMode === "fixed" && (
                    <>
                      <Input data-testid={`below-value-${p.sku}`} type="number" step="0.01" value={r.below} onChange={(e) => setBelowValue(p.sku, e.target.value)} className="w-24 font-mono font-semibold" placeholder="0" />
                      <span className="text-sm font-bold text-amber-300">zł</span>
                    </>
                  )}
                </div>
              </div>

              {r.haft && (
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wide">Dopłata za haft (zł/szt.)</Label>
                  <div className="flex flex-wrap gap-3">
                    {[["przod", "przód"], ["tyl", "tył"], ["oba", "przód+tył"]].map(([k, l]) => (
                      <div key={k} className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-muted-foreground w-16">{l}</span>
                        <Input data-testid={`haft-${k}-${p.sku}`} type="number" step="0.01" value={r.haft[k]} onChange={(e) => setHaft(p.sku, k, e.target.value)} className="w-20 font-mono font-semibold" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button data-testid="save-commissions-float" onClick={save} disabled={busy}
        className="fixed bottom-6 right-6 z-40 gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold uppercase tracking-wider shadow-lg shadow-black/40">
        <Save className="h-4 w-4" />{busy ? "Zapisywanie…" : "Zapisz ustawienia"}
      </Button>
    </div>
  );
};
